import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Phone, FileText, Download, ShieldCheck, Award, Maximize2 } from "lucide-react";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { BuyNowButton } from "@/components/BuyNowButton";
import { CardOrQuote } from "@/components/CardOrQuote";
import { ProductVideoHero } from "@/components/ProductVideoHero";
import { ProductGuideVideo } from "@/components/ProductGuideVideo";
import { ProductKits } from "@/components/ProductKits";
import { LaunchRibbon } from "@/components/LaunchRibbon";
import { StockBadge } from "@/components/StockBadge";
import Seo from "@/components/Seo";
import { Eyebrow } from "@/components/ui-bits";
import { PRODUCTS, INVERTER_PRODUCTS, PANEL_PRODUCTS, COMPANY, MANUFACTURER } from "@/data/site";
import NotFound from "@/pages/public/NotFound";

export default function ProductDetail() {
  const { slug } = useParams();
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const product = [...PRODUCTS, ...INVERTER_PRODUCTS, ...PANEL_PRODUCTS].find((p) => p.slug === slug);
  if (!product) return <NotFound />;

  const gallery = (product.images && product.images.length ? product.images : (product.image ? [product.image] : []));
  const isBlueProduct = ["cygni-all-in-one", "power-stack-pro-100", "power-box-g2", "stack100-pro-bdu"].includes(product.slug);


  const priceCard = (
    <div className="bg-ei-navy text-ei-white p-8 sm:p-10 lg:p-12 h-full flex flex-col justify-start" data-testid="product-price-card">
      <div className="ei-eyebrow text-ei-gold flex items-center gap-2">{product.fromPrice ? "System packs from" : "Wholesale price"} <StockBadge slug={product.slug} testId="stock-detail" /></div>
      {product.fromPrice ? (
        <>
          <div className="mt-3 font-display font-bold tnum text-4xl">{product.fromPrice}</div>
          <p className="mt-2 text-sm text-ei-white/70">Trade/wholesale pricing is confirmed on your quote. EI Battery supplies equipment only to licensed contractors.</p>
        </>
      ) : (
        <>
          <div className={`mt-3 font-display font-bold tnum ${product.hasPublicPrice ? "text-4xl" : "text-2xl text-ei-gold"}`}>
            <span data-testid="product-detail-price">{product.priceLabel}<span className="ml-1 text-sm font-normal text-current opacity-80">+ GST</span></span>
          </div>
          <p className="mt-2 text-sm text-ei-white/70">{product.priceNote}</p>
        </>
      )}
      <CardOrQuote slug={product.slug} quoteName={product.name} image={gallery[0]} includes={product.kit?.items} cardTestId="product-detail-buy-cta" quoteTestId="product-detail-quote-cta" dark className="mt-7" />
      <a href={COMPANY.phoneHref} className="mt-3 w-full font-display text-sm font-bold text-ei-white border border-ei-white/30 px-6 py-4 rounded-sm hover:bg-ei-white/10 transition-colors inline-flex items-center justify-center gap-2 min-h-[44px]">
        <Phone className="w-4 h-4" /> {COMPANY.phone}
      </a>
      <p className="mt-5 text-xs text-ei-white/50 leading-relaxed">
        Trade customers only. Eligibility is checked by our sales team. Supply-only — installation is completed by your business.
      </p>
    </div>
  );

  return (
    <>
      <Seo title={`${product.name} — EI Battery Solutions`} description={product.summary} />
      {product.video && <ProductVideoHero product={product} />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/products" className="inline-flex items-center gap-2 text-sm font-semibold text-ei-navy hover:text-ei-golddark" data-testid="back-to-products">
          <ArrowLeft className="w-4 h-4" /> All products
        </Link>
      </div>

      {/* Product photo gallery */}
      <section className="w-full" data-testid="product-top-media">
        <div className="grid items-stretch lg:grid-cols-3">
          {product.image && (
            <div className={`lg:col-span-2 relative overflow-hidden aspect-[4/3] sm:aspect-[3/2] flex items-center justify-center ${isBlueProduct ? "bg-ei-navy" : "bg-white"}`} data-testid="product-gallery">
              {!product.component && product.originalPrice && <LaunchRibbon testId="launch-ribbon-detail" />}
              <button type="button" onClick={() => setZoom(true)} aria-label="View photo full screen"
                className="group absolute inset-0 flex items-center justify-center cursor-zoom-in"
                data-testid="product-hero-zoom">
                <img src={gallery[Math.min(active, gallery.length - 1)]} alt={product.imageAlt || product.name} className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" data-testid="product-hero-image" />
                <span className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-ei-navy/80 px-3.5 py-2 font-display text-[11px] font-bold uppercase tracking-[0.15em] text-ei-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:opacity-80">
                  <Maximize2 className="h-3.5 w-3.5" /> Tap to enlarge
                </span>
              </button>
            </div>
          )}
          {product.video && <div className="lg:col-span-1">{priceCard}</div>}
        </div>
        {gallery.length > 1 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 flex gap-3 flex-wrap justify-center">
            {gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`View image ${i + 1}`}
                aria-pressed={i === active}
                className={`w-32 h-24 rounded-sm overflow-hidden border-2 transition-colors ${i === active ? "border-ei-gold" : "border-ei-mist hover:border-ei-navy/40"}`}
                data-testid={`product-thumb-${i}`}
              >
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </section>

      {zoom && (
        <PhotoLightbox
          images={gallery}
          index={Math.min(active, gallery.length - 1)}
          alt={product.imageAlt || product.name}
          onIndexChange={setActive}
          onClose={() => setZoom(false)}
        />
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 min-w-0">
          <Eyebrow>{product.category}</Eyebrow>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ei-navy">{product.name}</h1>
          {product.manufacturer && (
            <p className="mt-2 text-sm text-ei-graphite/60">Manufacturer reference: {product.manufacturer}</p>
          )}
          <p className="mt-5 text-lg text-ei-graphite/90 leading-relaxed">{product.positioning}</p>

          <h2 className="mt-10 text-xl font-semibold text-ei-navy">Wholesale highlights</h2>
          <ul className="mt-4 space-y-3">
            {product.highlights.map((h) => (
              <li key={h} className="flex items-start gap-3 text-ei-graphite/90">
                <Check className="w-5 h-5 text-ei-golddark mt-0.5 flex-shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>

          {product.guideVideo && (
            <ProductGuideVideo guide={product.guideVideo} poster={gallery[0]} productName={product.name} />
          )}

          {/* Specifications */}
          <div className="mt-10 bg-white border border-ei-mist rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-ei-mist"><h3 className="text-base font-semibold text-ei-navy">Specifications</h3></div>
            {product.specs.length > 0 ? (
              <table className="w-full text-sm table-fixed" data-testid="product-specs">
                <tbody className="divide-y divide-ei-mist">
                  {product.specs.map((s) => (
                    <tr key={s.label} className="flex flex-col sm:table-row">
                      <td className="px-4 sm:px-6 pt-3 pb-1 sm:py-3 text-ei-graphite/70 sm:w-1/2 break-words">{s.label}</td>
                      <td className="px-4 sm:px-6 pb-3 pt-0 sm:py-3 text-ei-navy font-medium tnum break-words">{s.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="px-6 py-5 text-sm text-ei-graphite/70">{product.specNote}</p>
            )}
          </div>
          {product.specs.length > 0 && (
            <p className="mt-3 text-xs text-ei-graphite/55 leading-relaxed border-l-2 border-ei-gold/60 pl-3">{product.specNote}</p>
          )}

          {/* Certifications */}
          {product.certifications.length > 0 && (
            <div className="mt-8">
              <h3 className="text-base font-semibold text-ei-navy flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-ei-golddark" /> Manufacturer certifications</h3>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="product-certifications">
                {product.certifications.map((c) => (
                  <span key={c} className="text-xs font-semibold text-ei-navy bg-ei-white border border-ei-mist rounded-sm px-2.5 py-1 tnum">{c}</span>
                ))}
              </div>
              {MANUFACTURER && (
                <p className="mt-3 text-xs text-ei-graphite/55">Certifications as listed by the manufacturer, {MANUFACTURER.name}.</p>
              )}
            </div>
          )}

          {/* What's included in the kit */}
          {product.kit && (
            <div className="mt-8 bg-ei-white border border-ei-mist rounded-md p-6" data-testid="product-kit">
              <h3 className="text-base font-semibold text-ei-navy">What's included in the kit</h3>
              {product.kit.note && <p className="mt-2 text-sm text-ei-graphite/85">{product.kit.note}</p>}
              <ul className="mt-4 space-y-2">
                {product.kit.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-ei-graphite/90">
                    <span className="text-ei-golddark font-bold leading-5">•</span> {it}
                  </li>
                ))}
              </ul>
              {product.kit.installer && (
                <p className="mt-4 text-xs text-ei-graphite/55 leading-relaxed border-l-2 border-ei-gold/60 pl-3">{product.kit.installer}</p>
              )}
            </div>
          )}

          {/* System packs */}
          {!product.batteryId && product.packs && product.packs.length > 0 && (
            <div className="mt-8" data-testid="product-packs">
              <h2 className="text-xl font-semibold text-ei-navy">System packs</h2>
              <p className="mt-2 text-sm text-ei-graphite/70 leading-relaxed">{product.packNote}</p>
              <div className="mt-5 space-y-4">
                {product.packs.map((pk, i) => (
                  <div key={i} className="bg-white border border-ei-mist rounded-md p-6" data-testid={`pack-${i}`}>
                    {pk.image && (
                      <div className="mb-5 rounded-md overflow-hidden bg-ei-navy aspect-[16/9]">
                        <img src={pk.image} alt={`${product.name} — ${pk.name}`} loading="lazy" className="w-full h-full object-cover" data-testid={`pack-image-${i}`} />
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        {pk.group && <div className="ei-eyebrow text-ei-golddark">{pk.group}</div>}
                        <h3 className="mt-1 text-base font-semibold text-ei-navy">{pk.name}</h3>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-display text-2xl font-bold text-ei-navy tnum" data-testid={`pack-price-${i}`}>{pk.price}</div>
                        <div className="text-[11px] text-ei-graphite/55">supply only, equipment price</div>
                      </div>
                    </div>
                    <ul className="mt-4 space-y-2">
                      {pk.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-sm text-ei-graphite/90">
                          <Check className="w-4 h-4 text-ei-golddark mt-0.5 flex-shrink-0" /> {it}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 flex flex-wrap items-center gap-4">
                      <BuyNowButton testId={`pack-buy-${i}`} label="Buy this pack" showQty={false} />
                      <Link to={`/request-a-quote?product=${encodeURIComponent(product.name + " — " + pk.name)}`} className="inline-flex items-center gap-2 text-sm font-bold text-ei-navy hover:text-ei-golddark" data-testid={`pack-cta-${i}`}>
                        Enquire about this pack <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 min-w-0">
          <div className="sticky top-24 space-y-6">
            {!product.video && priceCard}

            {/* Documents */}
            <div className="bg-white border border-ei-mist rounded-md p-6">
              <h3 className="text-base font-semibold text-ei-navy flex items-center gap-2"><FileText className="w-4 h-4 text-ei-golddark" /> Product documents</h3>
              {product.documents.length > 0 ? (
                <ul className="mt-4 space-y-2" data-testid="product-documents">
                  {product.documents.map((d) => (
                    <li key={d.file}>
                      <a href={d.file} target="_blank" rel="noopener noreferrer" download className="flex items-center justify-between gap-3 border border-ei-mist rounded-sm px-4 py-3 hover:border-ei-navy/40 hover:bg-ei-white transition-colors group min-h-[44px]" data-testid={`doc-${d.file.split("/").pop()}`}>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-ei-navy truncate">{d.label}</span>
                          <span className="block text-xs text-ei-graphite/55">{d.type} · PDF</span>
                        </span>
                        <Download className="w-4 h-4 text-ei-golddark group-hover:translate-y-0.5 transition-transform flex-shrink-0" />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-ei-graphite/70">Manufacturer documentation is being supplied and will appear here. Contact our team for documents in the meantime.</p>
              )}
              {product.manufacturer && (
                <p className="mt-4 text-xs text-ei-graphite/55">Documentation supplied by the manufacturer, {MANUFACTURER.name}.</p>
              )}
            </div>

            {/* Warranty */}
            <div className="bg-white border border-ei-mist rounded-md p-6">
              <h3 className="text-base font-semibold text-ei-navy flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-ei-golddark" /> Warranty</h3>
              <p className="mt-2 text-sm text-ei-graphite/85 leading-relaxed" data-testid="product-warranty">{product.warranty}</p>
            </div>

            {/* Manufacturer recognition */}
            {product.manufacturer && (
              <div className="bg-ei-white border border-ei-mist rounded-md p-6">
                <h3 className="text-base font-semibold text-ei-navy flex items-center gap-2"><Award className="w-4 h-4 text-ei-golddark" /> Proven manufacturer</h3>
                <p className="mt-2 text-sm text-ei-graphite/85 leading-relaxed">{MANUFACTURER.awards}</p>
                <p className="mt-2 text-xs text-ei-graphite/55">{MANUFACTURER.note}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Ready-made kits that use this battery, full page width */}
      {product.batteryId && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <ProductKits batteryId={product.batteryId} productName={product.name} />
        </section>
      )}
    </>
  );
}
